import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LabelIcon from '@mui/icons-material/Label';
import FolderIcon from '@mui/icons-material/Folder';
import CategoryIcon from '@mui/icons-material/Category';
import StarIcon from '@mui/icons-material/Star';
import FlagIcon from '@mui/icons-material/Flag';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorageIcon from '@mui/icons-material/Storage';
import { extractErrorMessage } from '../../../core/services';

// Fixed catalog of icons a user can pick for a tab, stored as the MUI icon
// name (frontend concern only — same convention as AiAssistantRole.avatarIcon).
const TAB_ICON_OPTIONS: { name: string; Icon: typeof LabelIcon }[] = [
  { name: 'Label', Icon: LabelIcon },
  { name: 'Folder', Icon: FolderIcon },
  { name: 'Category', Icon: CategoryIcon },
  { name: 'Star', Icon: StarIcon },
  { name: 'Flag', Icon: FlagIcon },
  { name: 'Bookmark', Icon: BookmarkIcon },
  { name: 'Business', Icon: BusinessIcon },
  { name: 'Group', Icon: GroupIcon },
  { name: 'Person', Icon: PersonIcon },
  { name: 'Public', Icon: PublicIcon },
  { name: 'TrendingUp', Icon: TrendingUpIcon },
  { name: 'Lightbulb', Icon: LightbulbIcon },
  { name: 'Build', Icon: BuildIcon },
  { name: 'Security', Icon: SecurityIcon },
  { name: 'AttachMoney', Icon: AttachMoneyIcon },
  { name: 'ShoppingCart', Icon: ShoppingCartIcon },
  { name: 'LocalOffer', Icon: LocalOfferIcon },
  { name: 'Storage', Icon: StorageIcon },
];
const TAB_ICON_MAP = new Map(TAB_ICON_OPTIONS.map((o) => [o.name, o.Icon]));

/** The icon a tab was saved with, looked up in the catalog above — nothing for a tab without one. */
export function TabIconGlyph({ icon }: { icon: string | null }) {
  const Icon = icon ? TAB_ICON_MAP.get(icon) : undefined;
  return Icon ? <Icon fontSize="small" /> : null;
}

/**
 * Name + icon picker for creating a new dashboard tab — shared by the Main
 * Entities and High Level Overview blocks of ProjectDetail.
 */
export function AddTabDialog({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  /** Rejects with the API error so the dialog can show it inline. */
  onSubmit: (name: string, icon: string | null) => Promise<unknown>;
}) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setIcon(null);
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(name.trim(), icon);
      onClose();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Tab</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Tab name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
          sx={{ mt: 0.5, mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Icon (optional)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {TAB_ICON_OPTIONS.map(({ name: iconName, Icon }) => (
            <IconButton
              key={iconName}
              onClick={() => setIcon((cur) => (cur === iconName ? null : iconName))}
              disabled={submitting}
              sx={{
                border: '1px solid',
                borderColor: icon === iconName ? 'primary.main' : 'divider',
                bgcolor: icon === iconName ? (theme) => alpha(theme.palette.primary.main, 0.12) : 'transparent',
              }}
            >
              <Icon fontSize="small" />
            </IconButton>
          ))}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!name.trim() || submitting}>
          {submitting ? 'Adding…' : 'Add Tab'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
